import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Glyph, type GlyphMeaning } from "@apf/design-system/glyphs";
import type { LifecycleStage } from "@apf/framework-config";
import { businessModulePath, type BusinessModule, type DomainWorkblock } from "../catalogue";
import { useCopy, useFramework } from "../framework";
import { HomeOverview } from "../components/HomeOverview";
import { OfficeProductCards } from "../components/OfficeProductCards";
import {
  ArrowLinkLabel,
  BeforeAfter,
  Door,
  Lockup,
  SectionHeading,
  StatementSection,
  StatusMark,
  Trail,
  ordinal
} from "../components/ui";
import { useCountUp, usePageTitle, useRevealOnce, useScrollToHash } from "./support";

/* Governed count lines keep their number in the copy template, so a locale
 * owns the phrasing around the figure. */
function replaceCount(template: string, count: number): string {
  return template.replace("{count}", String(count));
}

export function useFrameworkModel() {
  const { snapshot } = useFramework();
  return useMemo(() => {
    const { content, businessModuleRegistry } = snapshot;
    const stages = [...(content.stages ?? [])].sort((a, b) => a.order - b.order);
    const capabilityRows = [...(content.capabilityRows ?? [])].sort((a, b) => a.order - b.order);
    const domainWorkblockById = new Map<string, DomainWorkblock>(
      content.domainWorkblocks.map((workblock) => [workblock.id, workblock])
    );
    const capabilityById = new Map(content.capabilities.map((capability) => [capability.id, capability]));
    const businessModuleById = new Map<string, BusinessModule>(
      businessModuleRegistry.businessModules.map((module) => [module.id, module])
    );
    const businessModulesByDomainWorkblock = new Map<string, BusinessModule[]>();
    for (const domainWorkblock of content.domainWorkblocks) {
      businessModulesByDomainWorkblock.set(
        domainWorkblock.id,
        domainWorkblock.businessModuleIds.flatMap((id) => {
          const module = businessModuleById.get(id);
          return module ? [module] : [];
        })
      );
    }
    const stageByDomainWorkblock = new Map<string, LifecycleStage>();
    for (const stage of stages) {
      for (const domainWorkblockId of stage.domainWorkblockIds) {
        stageByDomainWorkblock.set(domainWorkblockId, stage);
      }
    }
    const businessModulePosition = new Map<string, number>(
      businessModuleRegistry.businessModules.map((module, index) => [module.id, index + 1])
    );
    return {
      stages,
      capabilityRows,
      domainWorkblockById,
      capabilityById,
      businessModuleById,
      businessModulesByDomainWorkblock,
      stageByDomainWorkblock,
      businessModulePosition
    };
  }, [snapshot]);
}

export const workblockStatusBadge: Record<string, GlyphMeaning> = {
  reference: "status.reference",
  pattern: "status.pattern",
  roadmap: "status.roadmap"
};

/**
 * One calm anatomy for every business-module row: per-view ordinal,
 * glyph and name on the left, the job statement and description each held to a
 * single line in the content column, the status chip pinned to the top-right
 * corner and the module's tool line to the bottom-right. The full job
 * statement and description live on the detail page; the row is an index entry.
 */
export function BusinessModuleIndexRow({
  businessModule,
  position,
  kicker,
  lead,
  description
}: {
  businessModule: BusinessModule;
  position: number;
  kicker?: string;
  lead?: string;
  description?: string;
}) {
  const copy = useCopy();
  return (
    <li className="apf-ruled-index__row">
      <Link
        className="apf-ruled-index__body apf-ruled-index__link"
        to={businessModulePath(businessModule.domainWorkblockId, businessModule.id)}
      >
        <span className="apf-ruled-index__ordinal">{ordinal(position)}</span>
        <div className="apf-ruled-index__heading">
          {kicker ? <span className="apf-ruled-index__kicker">{kicker}</span> : null}
          <h3 className="apf-ruled-index__name">
            <Glyph
              meaning="class.business-module"
              badge={workblockStatusBadge[businessModule.implementationStatus]}
              size={20}
              className="apf-heading-glyph"
            />
            {copy(businessModule.labelKey)}
          </h3>
        </div>
        <div className="apf-ruled-index__content">
          {lead ? (
            <p className="apf-ruled-index__lead">
              <Glyph meaning="class.job" size={16} className="apf-lead-glyph" />
              {lead}
            </p>
          ) : null}
          <p className="apf-ruled-index__description">{description ?? copy(businessModule.descriptionKey)}</p>
        </div>
        <div className="apf-ruled-index__meta">
          <StatusMark value={businessModule.implementationStatus} />
          <span className="apf-ruled-index__action">
            <span className="apf-ruled-index__tool">{copy(businessModule.toolKey)}</span>
            <Glyph meaning="utility.arrow-right" size={16} className="apf-ruled-index__arrow" />
          </span>
        </div>
      </Link>
    </li>
  );
}

/* One governed figure of the numbers band: the value renders complete and
 * counts up once when the band is first seen. */
function FactLedgerItem({ label, value, counting }: { label: string; value: number; counting: boolean }) {
  const display = useCountUp(value, counting);
  return (
    <div className="apf-fact-ledger__item">
      <dt className="apf-fact-ledger__label">{label}</dt>
      <dd className="apf-fact-ledger__value">{display}</dd>
    </div>
  );
}

/* The hero speaks the governed title verbatim; presentation alone lifts the
 * category phrase into the sanctioned gradient ink. When the copy changes and
 * the phrase disappears, the title simply renders whole. */
function HeroTitle({ text }: { text: string }) {
  const phrase = "agentic source-to-pay";
  const at = text.toLowerCase().indexOf(phrase);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="apf-home-hero__phrase">{text.slice(at, at + phrase.length)}</span>
      {text.slice(at + phrase.length)}
    </>
  );
}

export function FrameworkPage({ additionalDoor }: { additionalDoor?: ReactNode }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  useScrollToHash();
  const { stages, capabilityRows, domainWorkblockById, capabilityById, businessModulesByDomainWorkblock } = useFrameworkModel();
  const map = useRevealOnce<HTMLElement>();
  const doors = useRevealOnce<HTMLDivElement>();
  const ledger = useRevealOnce<HTMLDListElement>();
  const line = (key: string): string | undefined => snapshot.copy[key];
  const modules = snapshot.businessModuleRegistry.businessModules;
  /* One numbers band on the page: the six structural counts hold the ledger,
   * and the honest build statuses read as one governed sentence beneath it
   * rather than three more figure rows with chip labels standing in for
   * count labels. */
  const governedCounts: Array<[string, string, number]> = [
    ["facts.stages.label", "Stages", (snapshot.content.stages ?? []).length],
    ["facts.domains.label", "Domain Workblocks", snapshot.content.domainWorkblocks.length],
    ["facts.business-modules.label", "Business modules", modules.length],
    ["facts.flows.label", "Execution flows", modules.reduce((total, module) => total + module.executionFlows.length, 0)],
    ["facts.agents.label", "Governed agents", modules.reduce((total, module) => total + module.agents.length, 0)],
    ["facts.roles.label", "Roles", (snapshot.content.roles ?? []).length]
  ];
  const featured = (snapshot.content.opening?.featuredBusinessModuleIds ?? []).flatMap((id) => {
    const module = modules.find((candidate) => candidate.id === id);
    return module ? [module] : [];
  });
  return (
    <div className="apf-page apf-register-marketing apf-home">
      {/* The hero: a carbon field under one purple aura, the governed title at
        * the hero display scale with the gradient ink on the category phrase,
        * the two first actions, and the lifecycle map framed as the page's
        * product visual — the model itself, not an abstraction of it. */}
      <header className="apf-home-hero">
        <div className="apf-home-hero__inner">
          <Lockup compact />
          <h1 className="apf-home-hero__title"><HeroTitle text={copy("framework.title")} /></h1>
          <p className="apf-home-hero__description">{copy("framework.description")}</p>
          <div className="apf-home-hero__actions">
            <Link className="apf-button apf-home-hero__cta" to="/workblocks">{copy("opening.browse-catalogue", "Browse the Workblock catalogue")}</Link>
            <Link className="apf-home-hero__aside-link" to="/build#delivery-motion">
              <ArrowLinkLabel>{copy("opening.first-time")}</ArrowLinkLabel>
            </Link>
          </div>
        </div>
        <figure className={`apf-home-hero__visual${map.revealClass}`} ref={map.ref}>
          <figcaption className="apf-home-hero__visual-caption">
            <span className="apf-home-hero__visual-title">{copy("lifecycle.map-title", "Nine Domain Workblocks across five stages")}</span>
            <span className="apf-home-hero__visual-line">{line("lifecycle.caption") ?? "Nine domain Workblocks span source-to-pay. Each one contains five independently versioned business modules."}</span>
          </figcaption>
          <div className="apf-home-hero__visual-frame">
            <HomeOverview
              stages={stages}
              domainWorkblockById={domainWorkblockById}
              businessModulesByDomainWorkblock={businessModulesByDomainWorkblock}
              capabilityRows={capabilityRows}
              capabilityById={capabilityById}
            />
          </div>
        </figure>
      </header>

      {/* The opening argument, in reading order: the enterprise gap, the
        * shift it demands, what GroundWorks is, the practitioner doors, the
        * bounded first move, one worked case with the governed counts behind
        * it, and the Accenture delivery mechanism. Every paragraph is the
        * governed opening copy; the arc is the composition. */}
      <section className="apf-opening apf-opening--column apf-page-section" aria-labelledby="gap-title">
        <div className="apf-opening__letter apf-opening__letter--column">
          <p className="apf-section-heading__eyebrow">{copy("opening.gap-eyebrow", "The enterprise problem")}</p>
          <h2 id="gap-title">{copy("opening.gap-title", "The work that hurts is between systems")}</h2>
          <p>{copy("opening.letter.p1")}</p>
        </div>
      </section>

      <StatementSection id="shift" eyebrow={copy("opening.shift-eyebrow", "The shift")} headline={copy("opening.core-line")}>
        <p>{copy("opening.letter.p5")}</p>
      </StatementSection>

      {/* Definition and orientation are one thought — what GroundWorks is,
        * then the four questions answered — so they share one section rather
        * than reading as two short strips. */}
      <section className="apf-opening apf-opening--column apf-page-section apf-band apf-band--tint" aria-labelledby="definition-title">
        <div className="apf-opening__letter apf-opening__letter--column">
          <h2 id="definition-title">{copy("opening.definition-title", "What GroundWorks is")}</h2>
          <p>{copy("opening.letter.p2")}</p>
        </div>
        <dl className="apf-definition-list apf-orientation" aria-label={copy("page.framework.orientation-label", "Orientation")}>
          <div>
            <dt>{copy("orientation.what.label", "What it is")}</dt>
            <dd>{copy("orientation.what", "GroundWorks is the Beyond Work source-to-pay framework. Accenture GroundWorks is Accenture's platform implementation of it, tailored for AI-first procurement.")}</dd>
          </div>
          <div>
            <dt>{copy("orientation.covers.label", "What it covers")}</dt>
            <dd>{copy("orientation.covers", "The whole source-to-pay lifecycle: every stage, Domain Workblock, business module, record, role and authority boundary that connects a signal to a governed action.")}</dd>
          </div>
          <div>
            <dt>{copy("orientation.start.label", "Where to start")}</dt>
            <dd>{copy("orientation.start", "Start from the work you run, the role you hold, or the outcome you want to change.")} <Link to="/workblocks">{copy("page.framework.orientation.start.work", "The work")}</Link> · <Link to="/reference/roles">{copy("page.framework.orientation.start.roles", "The roles")}</Link> · <Link to="/reference/jobs">{copy("page.framework.orientation.start.outcomes", "The outcomes")}</Link></dd>
          </div>
          <div>
            <dt>{copy("orientation.use.label", "What to use it for")}</dt>
            <dd>{copy("orientation.use", "Scope a first bounded outcome, walk a client through the operating model, and hand delivery a contract it can build on.")}</dd>
          </div>
        </dl>
      </section>

      {/* Three equal job-shaped doors, one per permanent section. They match
        * the primary navigation exactly, so the home page teaches the whole
        * site's shape in one screen. The hub composes them as the doctrine's
        * door grid: three bordered cells with the sanctioned micro-lift. */}
      <section className="apf-page-section" aria-label={copy("page.framework.doors-label", "Choose your way in")}>
        <div className={`apf-door-list apf-door-list--grid${doors.revealClass}`} ref={doors.ref}>
          <Door
            to="/client-engagement"
            glyph="class.proof"
            index={ordinal(1)}
            title={copy("door.section.client-engagement.title", "Make the client case")}
            line={copy("door.section.client-engagement.body")}
            meta={replaceCount(copy("page.framework.count.domains", "{count} domains"), snapshot.content.domainWorkblocks.length)}
          />
          <Door
            to="/build"
            glyph="object.workblock"
            index={ordinal(2)}
            title={copy("door.section.build.title", "Understand the framework and build")}
            line={copy("door.section.build.body")}
            meta={replaceCount(copy("page.framework.count.business-modules", "{count} business modules"), modules.length)}
          />
          <Door
            to="/reference"
            glyph="class.record"
            index={ordinal(3)}
            title={copy("door.section.reference.title", "Inspect the complete model")}
            line={copy("door.section.reference.body")}
            meta={replaceCount(copy("page.framework.count.shared-entities", "{count} shared entities"), snapshot.groundworks.entities.length)}
          />
          {additionalDoor}
        </div>
      </section>

      {/* The first move and the Accenture delivery mechanism are one pitch:
        * start bounded, and reuse plus client tailoring carry the delivery.
        * One letter, one action row, instead of two separated strips. */}
      <section className="apf-opening apf-opening--column apf-page-section" aria-labelledby="first-move-title">
        <div className="apf-opening__letter apf-opening__letter--column">
          <h2 id="first-move-title">{copy("opening.first-move-title", "Start with one bounded outcome")}</h2>
          <p>{copy("opening.letter.p4")}</p>
          <p>{copy("page.build.method.description", "Reuse and client tailoring are the same asset. The domain Workblock prevents a blank-page start; its five business modules make the operating model configurable for the client.")}</p>
          <div className="apf-opening__actions">
            <Link className="apf-button" to="/workblocks">{copy("opening.browse-catalogue", "Browse the Workblock catalogue")}</Link>
            <Link className="apf-button apf-button--secondary" to="/build#delivery-motion">{copy("opening.first-move-link", "Read the delivery motion")}</Link>
            <Link className="apf-button apf-button--secondary" to="/client-engagement">{copy("door.section.client-engagement.title", "Make the client case")}</Link>
          </div>
        </div>
      </section>

      {/* The worked case and the governed counts behind it share one section:
        * the ledger is the page's single numbers band. */}
      <section className="apf-opening apf-opening--column apf-page-section apf-band apf-band--tint apf-band--aura" aria-labelledby="proof-title">
        <div className="apf-opening__letter apf-opening__letter--column">
          <h2 id="proof-title">{copy("opening.proof-title", "One case, worked through")}</h2>
          <p>{copy("opening.letter.p3")}</p>
        </div>
        <dl className={`apf-fact-ledger${ledger.revealClass}`} aria-label={copy("page.framework.numbers-label", "The framework in numbers")} ref={ledger.ref}>
          {governedCounts.map(([key, fallback, value]) => (
            <FactLedgerItem key={key} label={copy(key, fallback)} value={value} counting={ledger.revealClass !== ""} />
          ))}
        </dl>
        <p className="apf-fact-ledger__note">{copy("status.disclosure", "8 reference modules, 9 defined modules and 28 roadmap modules.")}</p>
      </section>

      {featured.length ? (
        <section className="apf-page-section" aria-labelledby="featured-title">
          <SectionHeading
            id="featured-title"
            title={copy("page.framework.featured.title", "Where agentic work moves the needle first")}
            description={copy("vocab.legend", "Three words carry every hand-off: the agent executes inside its envelope, a person reviews what it proposes, and a named person approves what commits.")}
          />
          <div className="apf-featured">
            {featured.map((businessModule, index) => {
              const metricLabel = businessModule.metrics[0]?.split(":")[0]?.trim();
              const metric = metricLabel && metricLabel.length <= 32 ? metricLabel : undefined;
              const posture = businessModule.autonomy.targetPosture;
              const vocab = posture.includes("supervision")
                ? copy("vocab.human-reviews", "Human reviews")
                : posture.includes("monitoring")
                  ? copy("vocab.agent-executes", "Agent executes")
                  : copy("vocab.human-approves", "Human approves");
              return (
                <Link className="apf-featured__row" key={businessModule.id} to={businessModulePath(businessModule.domainWorkblockId, businessModule.id)}>
                  <span className="apf-explainer-ordinal">{ordinal(index + 1)}</span>
                  <span className="apf-featured__name">{copy(businessModule.labelKey)}</span>
                  <span className="apf-featured__line">{copy(businessModule.jobStatementKey)}</span>
                  <span className="apf-featured__tags">
                    {metric ? <span className="apf-vocab-tag">{metric}</span> : null}
                    <span className="apf-vocab-tag">{vocab}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className="apf-page-section">
        <SectionHeading title={copy("office.catalogue.section-heading")} description={copy("office.catalogue.section-description")} />
        <OfficeProductCards />
      </section>
      <StatementSection id="method" eyebrow={copy("framework.positioning")} headline={copy("method.title")} wide>
        {/* Six positions, each a phase of the argument: every pair arms its
          * own entrance, so the section reads one shift at a time on the way
          * down rather than as a single block. */}
        {(["process", "unit", "ai", "learning", "change", "adoption"] as const).map((pair) => (
          <BeforeAfter
            key={pair}
            beforeLabel={copy("method.legacy-label")}
            afterLabel={copy("method.method-label")}
            before={copy(`method.${pair}.legacy`)}
            after={copy(`method.${pair}.method`)}
          />
        ))}
      </StatementSection>

      {/* The page closes on carbon: the commercial model and the three ways
        * onward share the closing band, so the last screen answers "what does
        * it cost" and "where do I go" on the framework's own field. */}
      <div className="apf-home-close">
        <section className="apf-price-band" aria-labelledby="pricing-label">
          <p className="apf-price-band__row">
            <span className="apf-price-band__label" id="pricing-label">{copy("framework.pricing-label")}</span>
            <span className="apf-price-band__line">{copy("commercial.pricing-headline")}</span>
          </p>
        </section>

        {/* The closing trail repeats the three doors, so the page ends on the
          * same three ways in that it opened with. */}
        <Trail
          label={copy("trail.label", "Where to go next")}
          items={[
            { to: "/client-engagement", glyph: "class.proof", label: copy("door.section.client-engagement.title", "Make the client case"), context: copy("door.section.client-engagement.body") },
            { to: "/build", glyph: "object.workblock", label: copy("door.section.build.title", "Understand the framework and build"), context: copy("door.section.build.body") },
            { to: "/reference", glyph: "class.record", label: copy("door.section.reference.title", "Inspect the complete model"), context: copy("door.section.reference.body") }
          ]}
        />
      </div>
    </div>
  );
}

